/**
 * IEM units needed — SINGLE SOURCE OF TRUTH.
 *
 * The rider's "Units needed" line and the IEM config page's transmitter summary
 * answer the SAME question ("how many transmitters does this band ask for?"), so
 * they must agree. They drifted (2026-09-17): the rider counted only the
 * selected MUSICIANS while the config page counted the selected musicians AND
 * crew — crew cards carry their own in-ear choice (MonitoringSection in
 * CrewEditPage) — so the document sent to the promoter asked for 4 units
 * instead of 6.
 *
 * Rule: every person SELECTED for the rider (musician or crew) whose in-ear
 * choice is not 'none' needs one transmitter. Spares / guests / an engineer
 * without in-ears belong to the manual "Extra bodypacks" field.
 */
export interface IemPerson {
  id?: string | null;
  name?: string | null;
  role?: string | null;
  color?: string | null;
  iemType?: string | null;
  /** Wired in-ears (no transmitter needed). */
  iemWired?: boolean | null;
}

/** true when this person uses in-ears (any value but missing / 'none'). */
export const usesInEars = (p: IemPerson | null | undefined): boolean =>
  !!p && !!p.iemType && p.iemType !== 'none';

const selectedOf = <T extends IemPerson>(
  list: T[] | null | undefined,
  ids: string[] | null | undefined,
): T[] => {
  const set = new Set(ids || []);
  return (list || []).filter((p) => !!p && p.id != null && set.has(String(p.id)));
};

/**
 * Selected people who need a transmitter, in document order (musicians first,
 * then crew — the order both the rider and the config page display).
 */
export function selectedIemPeople(
  musicians: IemPerson[] | null | undefined,
  crew: IemPerson[] | null | undefined,
  activeMusicianIds: string[] | null | undefined,
  activeCrewIds: string[] | null | undefined,
): IemPerson[] {
  return [
    ...selectedOf(musicians, activeMusicianIds),
    ...selectedOf(crew, activeCrewIds),
  ].filter(usesInEars);
}

/** true when this person's in-ears are WIRED (feed straight from the monitor
 *  rack): no transmitter and no bodypack to provide. */
export const isWired = (p: IemPerson | null | undefined): boolean => !!p && p.iemWired === true;

/** Selected people who need a TRANSMITTER (wireless in-ears), in document order. */
export function selectedTransmitterPeople(
  musicians: IemPerson[] | null | undefined,
  crew: IemPerson[] | null | undefined,
  activeMusicianIds: string[] | null | undefined,
  activeCrewIds: string[] | null | undefined,
): IemPerson[] {
  return selectedIemPeople(musicians, crew, activeMusicianIds, activeCrewIds).filter((p) => !isWired(p));
}

/** Selected people with WIRED in-ears (no transmitter needed), in document order. */
export function selectedWiredPeople(
  musicians: IemPerson[] | null | undefined,
  crew: IemPerson[] | null | undefined,
  activeMusicianIds: string[] | null | undefined,
  activeCrewIds: string[] | null | undefined,
): IemPerson[] {
  return selectedIemPeople(musicians, crew, activeMusicianIds, activeCrewIds).filter(isWired);
}

/**
 * Number of IEM UNITS NEEDED = transmitters to ask for = selected people with
 * in-ears MINUS those whose in-ears are wired (a wired feed needs no unit).
 */
export function countIemUnits(
  musicians: IemPerson[] | null | undefined,
  crew: IemPerson[] | null | undefined,
  activeMusicianIds: string[] | null | undefined,
  activeCrewIds: string[] | null | undefined,
): number {
  return selectedTransmitterPeople(musicians, crew, activeMusicianIds, activeCrewIds).length;
}
